import { create } from 'zustand'

export interface Toast {
  id: string
  type: 'info' | 'success' | 'error' | 'warning'
  title?: string
  message: string
  durationMs?: number | null
  createdAt: number
}

// Persistent notification shown in the navbar bell dropdown. Only WebSocket-
// pushed events live here; transient toasts from user actions do NOT.
export interface NotificationEntry {
  id: string
  type: 'info' | 'success' | 'error' | 'warning'
  title?: string
  message: string
  createdAt: number
}

export interface NotificationState {
  toasts: Toast[]
  notifications: NotificationEntry[]
  showToast: (t: Omit<Toast, 'id' | 'createdAt'>) => string
  dismissToast: (id: string) => void
  pushNotification: (n: Omit<NotificationEntry, 'id' | 'createdAt'>) => string
  dismissNotification: (id: string) => void
  clearNotifications: () => void
  clearAll: () => void
}

const MAX_NOTIFICATIONS = 50

let idCounter = 0

export const useNotificationStore = create<NotificationState>((set) => ({
  toasts: [],
  notifications: [],

  showToast: (opts) => {
    const id = `toast-${++idCounter}`
    const toast: Toast = {
      ...opts,
      id,
      createdAt: Date.now(),
    }

    set((state) => ({
      toasts: [...state.toasts, toast],
    }))

    return id
  },

  dismissToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }))
  },

  pushNotification: (opts) => {
    const id = `notif-${++idCounter}`
    const entry: NotificationEntry = {
      ...opts,
      id,
      createdAt: Date.now(),
    }
    set((state) => ({
      notifications: [...state.notifications, entry].slice(-MAX_NOTIFICATIONS),
    }))
    return id
  },

  dismissNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }))
  },

  clearNotifications: () => {
    set({ notifications: [] })
  },

  clearAll: () => {
    set({ toasts: [], notifications: [] })
  },
}))
